import React, { FC, Fragment, useMemo } from 'react';
import { useTranslate } from 'react-admin';

import { VirtualizedList } from '~/components';

import { time } from '~/utils';
import { Account, Column, Moment } from '~/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVirtualizedListBegin,
  getVirtualizedListFail,
  getVirtualizedListSuccess,
  selectors,
} from '~/ducks/virtualizedList';
import { getReportedBalances } from '~/api/balances';
import { ReportedBalance } from '~/types/account';
import ColoredAmountField from '../../../components/TransactionsHistory/cellComponents/ColoredAmountField';
import BalancesFilters from '~/resources/accounts/components/BalancesFilters';

export const resource = 'accountBalances';

interface BalancesTabProps {
  record: Account;
  history?: any;
}

const label = (key: string): string => `resources.accounts.show.balances.${key}`;

const BalancesTab: FC<BalancesTabProps> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const dispatch = useDispatch();
  const filters = useSelector(
    selectors.getFilters<{
      bankingDayFrom?: Moment;
      bankingDayTo?: Moment;
    }>(resource)
  );
  const list = useSelector(selectors.getList(resource));

  const loadNextPage = async () => {
    if (isNextPageLoading || (!list.cursors.next && list.data.length > 0) || !record) return;

    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));
      const { data } = await getReportedBalances({
        next: list.cursors.next,
        ...filters,
      });
      const responseData = {
        ...data,
        cursors: data.cursors,
        records: data.balances ?? [],
      };
      dispatch(getVirtualizedListSuccess({ resource, responseData, queryToken }));
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource, error: err, queryToken }));
    }
  };

  const columns: Column<ReportedBalance>[] = useMemo(
    () => [
      {
        selectCellData: (rowData: ReportedBalance) => time(rowData.bankingDay).format('LL'),
        header: translate(label('bankingDay')),
        style: {
          flexBasis: 150,
          minWidth: 110,
        },
      },
      ...record.balances.map(({ code }) => ({
        cellDataWrapperComponent: ColoredAmountField,
        selectCellData: (rowData: ReportedBalance) => ({
          value: rowData.balances[code],
          currency: record.currency,
        }),
        header: translate(
          `utils.balances.code.${record.profileType === 'INDIVIDUAL' ? 'individuals_' : ''}${code}`
        ),
        style: {
          flexBasis: 150,
          minWidth: 110,
        },
      })),
    ],
    [record.balances, record.currency, record.profileType, translate]
  );

  // const [showData, setShowData] = useState<Transaction | null>(null);
  // const handleShowOpen = (data: Transaction) => setShowData(data);
  // const handleShowClose = () => setShowData(null);

  const { hasNextPage, isNextPageLoading, data } = list;
  if (!record) return null;
  return (
    <Fragment>
      <BalancesFilters resource={resource} record={record} />
      <VirtualizedList<ReportedBalance>
        resource={resource}
        columns={columns}
        hasNextPage={hasNextPage}
        filters={filters}
        isNextPageLoading={isNextPageLoading}
        data={data}
        loadNextPage={loadNextPage}
      />
    </Fragment>
  );
};

export default BalancesTab;
